import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiGet, AuthError } from '../api';
import { useClient } from '../ClientContext';
import Icon from '../components/Icon';

/**
 * Audit Log.
 *
 * Three things were missing rather than wrong.
 *
 *   A date range.       An audit log with no way to ask "what happened last
 *                       week" is a list, not a log.
 *   A visible reason.   It lived in a title attribute — the least
 *                       discoverable place in HTML, and unreachable on touch.
 *                       It is the most important field on the row, so the row
 *                       now opens.
 *   A count.            You saw whatever the server returned with no way to
 *                       tell whether that was everything.
 *
 * The endpoint filters by outcome, search and client. Range and rule are
 * applied here, over what the server returned — which is why the header says
 * "of N loaded" rather than "of N total". If the server ever starts capping
 * the response, that wording stays honest; a date parameter on /decisions
 * would be the better fix.
 */

const OUTCOME = {
  APPROVED: { cls: 'approved', label: 'Approved' },
  FLAG:     { cls: 'flagged',  label: 'Flagged' },
  BLOCK:    { cls: 'blocked',  label: 'Blocked' },
  OBSERVE:  { cls: 'observe',  label: 'Observe' },
};

const RANGES = [[7, '7D'], [30, '30D'], [90, '90D'], [0, 'All']];

const money = (n) => `£${(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

const prettyRule = (name) =>
  name ? name.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : '';

const ageHours = (iso) => {
  if (!iso) return 0;
  const d = new Date(iso);
  return isNaN(d) ? 0 : (Date.now() - d.getTime()) / 36e5;
};

const when = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const h = ageHours(iso);
  if (h < 1) return 'just now';
  if (h < 24) return `${Math.round(h)}h ago`;
  if (h < 48) return 'yesterday';
  if (h < 24 * 8) return `${Math.round(h / 24)}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const exact = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export default function AuditLog() {
  const { selectedId, isAll } = useClient();

  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [outcome, setOutcome] = useState('ALL');
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(30);
  const [rule, setRule] = useState('ALL');
  const [open, setOpen] = useState(null);

  // Outcome, search and client go to the server so a search covers the whole
  // history rather than only the page already loaded. Debounced so typing does
  // not fire a request per keystroke.
  const load = useCallback((outcomeArg, term, clientId) => {
    const params = new URLSearchParams();
    if (outcomeArg && outcomeArg !== 'ALL') params.set('outcome', outcomeArg);
    if (term) params.set('search', term);
    if (clientId && clientId !== 'ALL') params.set('client_id', clientId);
    const qs = params.toString();

    apiGet(`/decisions${qs ? `?${qs}` : ''}`)
      .then((data) => {
        setDecisions(data.decisions || []);
        setError(null);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof AuthError ? err.message : err.message || 'Could not load the audit log.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(outcome, search, selectedId), search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [outcome, search, selectedId, load]);

  useEffect(() => { setOpen(null); }, [outcome, search, days, rule, selectedId]);

  const rules = useMemo(
    () => Array.from(new Set(decisions.map((d) => d.rule_triggered).filter(Boolean))).sort(),
    [decisions]
  );

  const rows = useMemo(() => decisions.filter((r) => {
    if (days > 0 && ageHours(r.timestamp) > days * 24) return false;
    if (rule !== 'ALL' && r.rule_triggered !== rule) return false;
    return true;
  }), [decisions, days, rule]);

  const countBy = (o) => rows.filter((r) => r.outcome === o).length;
  const protectedTotal = rows.reduce((sum, r) => sum + (r.spend_protected || 0), 0);

  const exportCSV = () => {
    const headers = ['Action ID', 'Timestamp', 'Client', 'Campaign', 'Outcome',
                     'Rule triggered', 'Reason', 'Spend value', 'Spend protected'];
    const body = rows.map((r) => [
      r.action_id, r.timestamp, r.client_name, r.campaign_name, r.outcome,
      r.rule_triggered || '', (r.reason || '').replace(/"/g, '""'),
      r.spend_value, r.spend_protected,
    ]);
    // Every field quoted: campaign names and rule reasons contain commas, and
    // an unquoted export silently shifts columns.
    const csv = [headers, ...body]
      .map((line) => line.map((v) => `"${v ?? ''}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `bidsense-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="page-header split">
        <div>
          <h1 className="page-title">Audit Log</h1>
          <p className="page-subtitle">
            Every action an agent proposed, the rules it was checked against, and what was
            decided. Nothing here is editable.
          </p>
        </div>
        <div className="hdr-actions">
          <button className="btn btn-secondary" onClick={exportCSV} disabled={rows.length === 0}>
            <Icon name="download" size={14} strokeWidth={2.2} />
            Export {rows.length} row{rows.length === 1 ? '' : 's'}
          </button>
        </div>
      </div>

      {error && (
        <div className="banner bad">
          <Icon name="alert" size={16} strokeWidth={2.2} />
          <span>{error}</span>
        </div>
      )}

      <div className="filterbar">
        <span className="lbl">Range</span>
        <div className="seg" role="group" aria-label="Date range">
          {RANGES.map(([d, label]) => (
            <button key={d} aria-pressed={days === d} onClick={() => setDays(d)}>{label}</button>
          ))}
        </div>

        <span className="lbl">Outcome</span>
        <div className="seg" role="group" aria-label="Outcome">
          {[['ALL', 'All'], ['APPROVED', 'Approved'], ['FLAG', 'Flagged'], ['BLOCK', 'Blocked']].map(([k, label]) => (
            <button key={k} aria-pressed={outcome === k} onClick={() => setOutcome(k)}>{label}</button>
          ))}
        </div>

        <select value={rule} onChange={(e) => setRule(e.target.value)} aria-label="Rule triggered">
          <option value="ALL">Any rule</option>
          {rules.map((r) => <option key={r} value={r}>{prettyRule(r)}</option>)}
        </select>

        <label className="search">
          <Icon name="search" size={14} strokeWidth={2.2} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search campaigns or rules"
            aria-label="Search the audit log"
          />
        </label>

        <span className="showing">
          {loading ? 'Loading…' : `${rows.length} of ${decisions.length} loaded`}
        </span>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card pine">
          <div className="kpi-label">Spend protected</div>
          <div className="kpi-value">{money(protectedTotal)}</div>
          <div className="kpi-sub">Across {countBy('BLOCK')} blocked actions in this view</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Approved</div>
          <div className="kpi-value" style={{ color: 'var(--ok)' }}>{countBy('APPROVED')}</div>
          <div className="kpi-sub">Passed every active rule</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Flagged</div>
          <div className="kpi-value" style={{ color: 'var(--warn)' }}>{countBy('FLAG')}</div>
          <div className="kpi-sub">Held for human review</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Blocked</div>
          <div className="kpi-value" style={{ color: 'var(--bad)' }}>{countBy('BLOCK')}</div>
          <div className="kpi-sub">Never reached the platform</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Action history</span>
          <span className="card-note">Click a row for the full record</span>
        </div>

        {loading ? (
          <div style={{ padding: 'var(--space-5)' }}>
            {[80, 60, 80, 40, 60, 80].map((w, i) => (
              <div key={i} className={`skeleton skeleton-line w${w}`} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="empty">
            <h3>{search || outcome !== 'ALL' || rule !== 'ALL' || days ? 'No matching actions' : 'No actions yet'}</h3>
            <p>
              {search || outcome !== 'ALL' || rule !== 'ALL'
                ? 'Nothing in this range matches those filters. Widen the range or clear the search.'
                : 'Every action an agent proposes will be recorded here.'}
            </p>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="data-table dense">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Campaign</th>
                  {isAll && <th>Client</th>}
                  <th>Rule triggered</th>
                  <th>Outcome</th>
                  <th className="num">Daily budget</th>
                  <th className="num">Protected</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const o = OUTCOME[r.outcome] || { cls: '', label: r.outcome };
                  const isOpen = open === r.action_id;
                  const cols = isAll ? 7 : 6;
                  return (
                    <React.Fragment key={r.action_id}>
                      <tr
                        className={`expandable ${isOpen ? 'open' : ''}`}
                        onClick={() => setOpen(isOpen ? null : r.action_id)}
                      >
                        <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          <span className="exp-ico"><Icon name="chevron" size={12} strokeWidth={2.6} /></span>
                          {when(r.timestamp)}
                        </td>
                        <td style={{ fontWeight: 500 }}>{r.campaign_name}</td>
                        {isAll && <td style={{ color: 'var(--text-muted)' }}>{r.client_name}</td>}
                        <td>
                          {r.rule_triggered
                            ? <span className="tag-mono">{r.rule_triggered}</span>
                            : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                        </td>
                        <td><span className={`pill ${o.cls}`}>{o.label}</span></td>
                        <td className="num">{money(r.spend_value)}</td>
                        <td
                          className="num"
                          style={r.spend_protected > 0
                            ? { fontWeight: 700, color: 'var(--ok)' }
                            : { color: 'var(--text-muted)' }}
                        >
                          {r.spend_protected > 0 ? money(r.spend_protected) : '—'}
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="rowdetail">
                          <td colSpan={cols}>
                            <div className="rd-in">
                              <div>
                                <h5>What happened</h5>
                                <p>{r.reason || 'No reason recorded — the action passed every active rule.'}</p>
                              </div>
                              <div>
                                <h5>Record</h5>
                                <div className="rv-spec">
                                  <div><span className="k">Recorded</span><span className="v">{exact(r.timestamp)}</span></div>
                                  <div><span className="k">Outcome</span><span className="v">{o.label}</span></div>
                                  {r.rule_triggered && (
                                    <div><span className="k">Rule</span><span className="v">{prettyRule(r.rule_triggered)}</span></div>
                                  )}
                                  <div><span className="k">Action id</span><span className="v tag-mono">{r.action_id}</span></div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}